/**
 * Viewport camera math: pose, projection and Unreal-style navigation
 * transitions (SPEC §4.6), all pure functions over an immutable ViewCamera.
 *
 * Conventions match the engine: Y-up, meters, degrees. Yaw turns about +Y
 * (positive = left), pitch tilts about the camera's right axis
 * (positive = up), and yaw 0 / pitch 0 looks down −Z, like three's cameras.
 *
 * The camera orbits and dollies around a focus point `focusDistance` ahead of it.
 * F-framing sets that distance, so Alt+LMB orbits around what was framed.
 *
 * Projection and direction are independent, as in Blender and Unity: either
 * projection can look any way, and the axis views (Top, Front, Left, …) are
 * just yaw/pitch presets. An orthographic camera shows `orthoHeight` meters
 * vertically; there, the wheel zooms by changing that height.
 */
import { fittedVerticalFov } from "../../render/cameraFit";
import {
  mat4LookAt,
  mat4Invert,
  mat4Multiply,
  mat4Orthographic,
  mat4Perspective,
  mat4TransformPoint,
} from "../../core/math/mat4";
import type { Mat4 } from "../../core/math/mat4";
import { aabbCenter, aabbSize } from "../../core/math/aabb";
import type { Aabb } from "../../core/math/aabb";
import {
  vec3Add,
  vec3Cross,
  vec3Dot,
  vec3Length,
  vec3Normalize,
  vec3Scale,
  vec3Sub,
} from "../../core/math/vec3";
import type { Vec3 } from "../../core/math/vec3";
import type { CameraDesc } from "../../render/proxies";

export type Projection = "perspective" | "orthographic";
export type AxisView = "top" | "bottom" | "left" | "right" | "front" | "back";

export interface ViewCamera {
  readonly position: Vec3;
  /** Degrees about +Y. */
  readonly yaw: number;
  /** Degrees: ±MAX_PITCH when navigating, exactly ±90 in the Top and Bottom views. */
  readonly pitch: number;
  /** Vertical field of view, degrees (perspective). */
  readonly fov: number;
  readonly near: number;
  readonly far: number;
  /** Distance to the orbit/dolly pivot in front of the camera. */
  readonly focusDistance: number;
  readonly projection: Projection;
  /** Meters shown top to bottom when orthographic (its zoom). */
  readonly orthoHeight: number;
}

/**
 * The axis views as directions. Front looks down -Z, the way levels are
 * authored to be seen (their cameras sit on +Z); Top looks straight down with
 * -Z up on screen.
 */
const AXIS_VIEWS: Readonly<Record<AxisView, { yaw: number; pitch: number }>> = {
  top: { yaw: 0, pitch: -90 },
  bottom: { yaw: 0, pitch: 90 },
  front: { yaw: 0, pitch: 0 },
  back: { yaw: 180, pitch: 0 },
  left: { yaw: -90, pitch: 0 },
  right: { yaw: 90, pitch: 0 },
};

/**
 * An orthographic camera draws from this far behind its focus point, so
 * nothing near the camera is clipped; the picture doesn't depend on distance.
 */
const ORTHO_BACKOFF = 100;
const MIN_ORTHO_HEIGHT = 0.5;
const MAX_ORTHO_HEIGHT = 400;

/** Fly speed in m/s for camera speed levels 1–8 (Unreal's speed slider). Index 0 is unused. */
export const SPEED_BY_LEVEL = [0, 0.5, 1, 2, 4, 6, 9, 13, 20] as const;

/** Holding Shift while flying multiplies the speed, as in Unity's Scene view flythrough. */
export const FAST_FLY_MULTIPLIER = 3;

const WORLD_UP: Vec3 = [0, 1, 0];
const MAX_PITCH = 89;
const LOOK_DEGREES_PER_PIXEL = 0.2;
const MIN_FOCUS_DISTANCE = 0.25;
/** Each wheel notch moves this fraction of the way to the focus point. */
const DOLLY_FRACTION = 0.15;
/** Framing leaves a little margin around the bounds. */
const FRAME_MARGIN = 1.15;
/** Snapped to an axis view within this many degrees. */
const AXIS_EPSILON = 0.01;

const DEG = Math.PI / 180;

// ─── Orientation ─────────────────────────────────────────────────────────

type Orientation = Pick<ViewCamera, "yaw" | "pitch">;

export function forwardOf(camera: Orientation): Vec3 {
  const yaw = camera.yaw * DEG;
  const pitch = camera.pitch * DEG;
  return [-Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch)];
}

/**
 * From yaw alone, so it stays well defined looking straight up or down (a
 * cross product with world up would vanish there).
 */
export function rightOf(camera: Orientation): Vec3 {
  const yaw = camera.yaw * DEG;
  return [Math.cos(yaw), 0, -Math.sin(yaw)];
}

export function upOf(camera: Orientation): Vec3 {
  return vec3Cross(rightOf(camera), forwardOf(camera));
}

/** The point the camera orbits and dollies around. */
export function focusPoint(camera: ViewCamera): Vec3 {
  return vec3Add(camera.position, vec3Scale(forwardOf(camera), camera.focusDistance));
}

/** Position, yaw and pitch of a camera entity from its world matrix (its view looks down local −Z). */
export function poseFromMatrix(world: Mat4): Pick<ViewCamera, "position" | "yaw" | "pitch"> {
  const forward = vec3Normalize([-(world[8] ?? 0), -(world[9] ?? 0), -(world[10] ?? 1)]);
  return {
    position: [world[12] ?? 0, world[13] ?? 0, world[14] ?? 0],
    yaw: Math.atan2(-forward[0], -forward[2]) / DEG,
    pitch: Math.asin(Math.max(-1, Math.min(1, forward[1]))) / DEG,
  };
}

/**
 * The view from a camera entity: its pose and field of view, as a perspective
 * view, keeping the rest of `view` (clip planes, focus distance). Looking
 * through a camera, its preview and the level's startup camera all use it.
 */
export function lookingThrough(view: ViewCamera, world: Mat4, fov: number): ViewCamera {
  return { ...view, ...poseFromMatrix(world), fov, projection: "perspective" };
}

/**
 * How far ahead of a camera entity the editor's own view starts when it takes
 * that camera's view (a level's opening shot, Align View to Camera), in
 * meters: the framing is the camera's, near enough, and the camera's gizmo is
 * behind the eye rather than around it.
 */
export const START_AHEAD_OF_CAMERA = 0.5;

/**
 * The editor view taking a camera entity's view: looking through it, then
 * moved START_AHEAD_OF_CAMERA along the view. The viewport stays its own free
 * camera; piloting is what moves a camera entity.
 */
export function viewFromCamera(view: ViewCamera, world: Mat4, fov: number): ViewCamera {
  const through = lookingThrough(view, world, fov);
  return {
    ...through,
    position: vec3Add(through.position, vec3Scale(forwardOf(through), START_AHEAD_OF_CAMERA)),
  };
}

/** What the render backend needs to draw a view. */
export function describeView(camera: ViewCamera): CameraDesc {
  return {
    view: viewMatrix(camera),
    position: eyeOf(camera),
    fov: camera.fov,
    orthographicHeight: camera.projection === "orthographic" ? camera.orthoHeight : null,
    near: camera.near,
    far: camera.far,
  };
}

// ─── Axis views and projection ───────────────────────────────────────────

/** Turns to an axis view around the focus point, keeping the projection. */
export function snapToAxis(camera: ViewCamera, axis: AxisView): ViewCamera {
  const focus = focusPoint(camera);
  const turned = { ...camera, ...AXIS_VIEWS[axis] };
  return {
    ...turned,
    position: vec3Sub(focus, vec3Scale(forwardOf(turned), camera.focusDistance)),
  };
}

/** The axis view the camera is snapped to, or null for a free direction. */
export function axisViewOf(camera: Orientation): AxisView | null {
  const near = (a: number, b: number) =>
    Math.abs(((((a - b) % 360) + 540) % 360) - 180) < AXIS_EPSILON;
  const match = (Object.entries(AXIS_VIEWS) as [AxisView, Orientation][]).find(
    ([, view]) => near(camera.yaw, view.yaw) && Math.abs(camera.pitch - view.pitch) < AXIS_EPSILON,
  );
  return match?.[0] ?? null;
}

/**
 * Switches projection keeping the focus point centered and the same height
 * visible there: orthographic shows what perspective showed at the focus
 * distance, and back.
 */
export function withProjection(camera: ViewCamera, projection: Projection): ViewCamera {
  if (projection === camera.projection) return camera;
  const halfFov = Math.tan((camera.fov * DEG) / 2);

  if (projection === "orthographic") {
    return { ...camera, projection, orthoHeight: 2 * camera.focusDistance * halfFov };
  }
  const focus = focusPoint(camera);
  const focusDistance = Math.max(MIN_FOCUS_DISTANCE, camera.orthoHeight / (2 * halfFov));
  return {
    ...camera,
    projection,
    focusDistance,
    position: vec3Sub(focus, vec3Scale(forwardOf(camera), focusDistance)),
  };
}

// ─── View and projection matrices ────────────────────────────────────────

/** Where the view is drawn from: the camera, or well behind the focus when orthographic. */
export function eyeOf(camera: ViewCamera): Vec3 {
  if (camera.projection === "perspective") return camera.position;
  return vec3Sub(focusPoint(camera), vec3Scale(forwardOf(camera), ORTHO_BACKOFF));
}

export function viewMatrix(camera: ViewCamera): Mat4 {
  const eye = eyeOf(camera);
  return mat4LookAt(eye, vec3Add(eye, forwardOf(camera)), upOf(camera));
}

/** The view's projection. Perspective keeps its width on narrow screens, as the renderer draws it (cameraFit). */
export function projectionMatrix(camera: ViewCamera, aspect: number): Mat4 {
  return camera.projection === "perspective"
    ? mat4Perspective(fittedVerticalFov(camera.fov, aspect) * DEG, aspect, camera.near, camera.far)
    : mat4Orthographic(camera.orthoHeight, aspect, camera.near, camera.far);
}

export interface ScreenPoint {
  /** CSS pixels from the viewport's top-left. */
  readonly x: number;
  readonly y: number;
  /** False when the point is behind the camera. */
  readonly visible: boolean;
}

/** World → viewport pixels, computed engine-side so overlays never query the renderer (A-40). */
export function projectPoint(
  camera: ViewCamera,
  point: Vec3,
  width: number,
  height: number,
): ScreenPoint {
  const inFront = vec3Dot(vec3Sub(point, eyeOf(camera)), forwardOf(camera)) > 0;

  const clip = mat4TransformPoint(
    mat4Multiply(projectionMatrix(camera, width / Math.max(1, height)), viewMatrix(camera)),
    point,
  );
  return { x: ((clip[0] + 1) / 2) * width, y: ((1 - clip[1]) / 2) * height, visible: inFront };
}

export interface Ray {
  readonly origin: Vec3;
  /** Unit length. */
  readonly direction: Vec3;
}

/**
 * The world-space ray through a viewport pixel (CSS px from the top left):
 * from the eye in perspective, and parallel to the view from the near plane
 * in orthographic. Unprojects two depths through the same matrices as
 * `projectPoint`, so the two agree.
 */
export function screenRay(
  camera: ViewCamera,
  x: number,
  y: number,
  width: number,
  height: number,
): Ray {
  const clipToWorld = mat4Invert(
    mat4Multiply(projectionMatrix(camera, width / Math.max(1, height)), viewMatrix(camera)),
  );
  const ndcX = (x / Math.max(1, width)) * 2 - 1;
  const ndcY = 1 - (y / Math.max(1, height)) * 2;
  if (!clipToWorld) return { origin: eyeOf(camera), direction: forwardOf(camera) };

  const near = mat4TransformPoint(clipToWorld, [ndcX, ndcY, -1]);
  const far = mat4TransformPoint(clipToWorld, [ndcX, ndcY, 1]);
  const direction = vec3Normalize(vec3Sub(far, near));
  return { origin: camera.projection === "perspective" ? eyeOf(camera) : near, direction };
}

/** Where a ray meets the ground plane (y = 0), or null when it runs level or away from it. */
export function groundPoint(ray: Ray): Vec3 | null {
  const dy = ray.direction[1];
  if (Math.abs(dy) < 1e-6) return null;
  const t = -ray.origin[1] / dy;
  return t > 0 ? vec3Add(ray.origin, vec3Scale(ray.direction, t)) : null;
}

// ─── Navigation transitions ──────────────────────────────────────────────

export interface FlyInput {
  /** −1..1: S/W. */
  readonly forward: number;
  /** −1..1: A/D. */
  readonly right: number;
  /** −1..1: Q/E. */
  readonly up: number;
}

/** RMB + WASD/QE: move along the camera's axes (Q/E along world up, as in Unreal). */
export function fly(
  camera: ViewCamera,
  input: FlyInput,
  dt: number,
  speedLevel: number,
  multiplier = 1,
): ViewCamera {
  const speed = SPEED_BY_LEVEL[clampLevel(speedLevel)] * multiplier * dt;
  const move = vec3Add(
    vec3Add(
      vec3Scale(forwardOf(camera), input.forward * speed),
      vec3Scale(rightOf(camera), input.right * speed),
    ),
    vec3Scale(WORLD_UP, input.up * speed),
  );
  return { ...camera, position: vec3Add(camera.position, move) };
}

/** RMB drag: turn in place. */
export function look(camera: ViewCamera, dx: number, dy: number): ViewCamera {
  return {
    ...camera,
    yaw: camera.yaw - dx * LOOK_DEGREES_PER_PIXEL,
    pitch: clampPitch(camera.pitch - dy * LOOK_DEGREES_PER_PIXEL),
  };
}

/** Alt + LMB: turn around the focus point, keeping the distance to it. */
export function orbit(camera: ViewCamera, dx: number, dy: number): ViewCamera {
  const pivot = focusPoint(camera);
  const turned = look(camera, dx, dy);
  return {
    ...turned,
    position: vec3Sub(pivot, vec3Scale(forwardOf(turned), camera.focusDistance)),
  };
}

/** MMB drag: slide in the view plane so the scene follows the cursor at the focus depth. */
export function pan(
  camera: ViewCamera,
  dx: number,
  dy: number,
  viewportHeight: number,
): ViewCamera {
  const metersPerPixel = visibleHeight(camera) / Math.max(1, viewportHeight);
  const offset = vec3Add(
    vec3Scale(rightOf(camera), -dx * metersPerPixel),
    vec3Scale(upOf(camera), dy * metersPerPixel),
  );
  return { ...camera, position: vec3Add(camera.position, offset) };
}

/**
 * Mouse wheel: move toward (positive) or away from the focus point, never past
 * it. Orthographic zooms by showing less (or more) height instead, since
 * moving closer wouldn't change the picture.
 */
export function dolly(camera: ViewCamera, notches: number): ViewCamera {
  const scale = 1 - DOLLY_FRACTION * notches;
  if (camera.projection === "orthographic")
    return withOrthoHeight(camera, camera.orthoHeight * scale);
  return atFocusDistance(camera, camera.focusDistance * scale);
}

/**
 * Pinch: zoom by the fingers' spread (`factor` is the new distance between
 * them over the old). Spreading them twice as far halves the distance to the
 * focus point, so the scene tracks the fingers; orthographic shows half the
 * height instead.
 */
export function zoom(camera: ViewCamera, factor: number): ViewCamera {
  if (!(factor > 0)) return camera;
  if (camera.projection === "orthographic")
    return withOrthoHeight(camera, camera.orthoHeight / factor);
  return atFocusDistance(camera, camera.focusDistance / factor);
}

/** Moves along the view toward the focus point until it's `distance` away (never past it). */
function atFocusDistance(camera: ViewCamera, distance: number): ViewCamera {
  const clamped = Math.max(MIN_FOCUS_DISTANCE, distance);
  const moved = camera.focusDistance - clamped;
  return {
    ...camera,
    position: vec3Add(camera.position, vec3Scale(forwardOf(camera), moved)),
    focusDistance: clamped,
  };
}

function withOrthoHeight(camera: ViewCamera, height: number): ViewCamera {
  return { ...camera, orthoHeight: Math.max(MIN_ORTHO_HEIGHT, Math.min(MAX_ORTHO_HEIGHT, height)) };
}

/**
 * F: keep the viewing direction and back off until the bounds fill the view.
 * Orthographic also fits them in the visible height.
 */
export function frameBounds(camera: ViewCamera, bounds: Aabb): ViewCamera {
  const center = aabbCenter(bounds);
  const radius = Math.max(0.25, vec3Length(aabbSize(bounds)) / 2);
  const distance = (radius / Math.sin((camera.fov * DEG) / 2)) * FRAME_MARGIN;

  return {
    ...camera,
    position: vec3Sub(center, vec3Scale(forwardOf(camera), distance)),
    focusDistance: distance,
    orthoHeight:
      camera.projection === "orthographic" ? 2 * radius * FRAME_MARGIN : camera.orthoHeight,
  };
}

/** Meters visible top to bottom at the focus point. */
function visibleHeight(camera: ViewCamera): number {
  return camera.projection === "orthographic"
    ? camera.orthoHeight
    : 2 * camera.focusDistance * Math.tan((camera.fov * DEG) / 2);
}

function clampPitch(pitch: number): number {
  return Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pitch));
}

function clampLevel(level: number): 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 {
  return Math.max(1, Math.min(8, Math.round(level))) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
}
